import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Chip,
  Avatar,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Paper,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete
} from '@mui/material';
import {
  Person,
  Add,
  Delete,
  Search,
  VpnKey,
  Refresh
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { dataverseService } from '../services/dataverseService';
import { handleDataverseLoadError, getDataverseErrorMessage } from '../lib/dataverseErrorHandler';
import { loadDataverseWithDemoFallback } from '../lib/loadDataverseData';
import KeyRequestDialog from '../components/KeyRequestDialog';
import { KEY_REQUEST_SCOPE } from '../config/keyRequest';
import { geocodeAddress } from '../services/geocodeService';
import { formatDistance, partitionNearbyLocks } from '../lib/geo';

const NEARBY_RADIUS_OPTIONS = [200, 500, 1000];
const PLACEHOLDER_ADDRESS = /^(ikke oppgitt|ukjent|n\/a|-)$/i;

const ResidentAssignmentCard = ({ resident, assignments, locks, onRemoveAssignment, onManageKeys }) => {
  const residentAssignments = assignments.filter(a => a.residentId === resident.residentId);
  const assignedLocks = locks.filter(lock => 
    residentAssignments.some(a => a.lockId === lock.lockId)
  );

  return (
    <Card className="card-hover fade-in">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
              <Person />
            </Avatar>
            <Box>
              <Typography variant="h6" component="div" fontWeight="600">
                {resident.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {resident.room}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={`${assignedLocks.length} lås(er)`}
            color="primary"
            size="small"
          />
        </Box>

        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 1 }}>
          Tilordnede låser:
        </Typography>
        
        <Grid container spacing={1}>
          {assignedLocks.map((lock) => {
            const assignment = residentAssignments.find(a => a.lockId === lock.lockId);
            return (
              <Grid item xs={12} sm={6} key={lock.id}>
                <Card variant="outlined" sx={{ p: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" fontWeight="500">
                        {lock.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {lock.location} • {lock.type}
                      </Typography>
                    </Box>
                    <IconButton 
                      color="error" 
                      onClick={() => onRemoveAssignment(assignment.id)}
                      size="small"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {assignedLocks.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
            Ingen låser tilordnet
          </Typography>
        )}

        <Button
          size="small"
          variant="outlined"
          startIcon={<VpnKey />}
          sx={{ mt: 2 }}
          onClick={() => onManageKeys(resident)}
          disabled={assignedLocks.length === 0}
        >
          Nøkler til alle lås
        </Button>
      </CardContent>
    </Card>
  );
};

const AssignmentsPage = () => {
  const navigate = useNavigate();
  const [residents, setResidents] = useState([]);
  const [locks, setLocks] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedResident, setSelectedResident] = useState('');
  const [selectedLocks, setSelectedLocks] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [lockSearchTerm, setLockSearchTerm] = useState('');
  const [residentOrigin, setResidentOrigin] = useState(null);
  const [geocodeStatus, setGeocodeStatus] = useState('idle');
  const [nearbyRadiusM, setNearbyRadiusM] = useState(500);
  
  // New state for confirmation dialogs and loading
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [assignmentToRemove, setAssignmentToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);
  const [keyDialogOpen, setKeyDialogOpen] = useState(false);
  const [keyDialogResident, setKeyDialogResident] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await loadDataverseWithDemoFallback(
        () => dataverseService.getAssignmentsData(),
        async () => {
          const { mockApi } = await import('../data/demoData');
          const [residentsData, locksData, assignmentsData] = await Promise.all([
            mockApi.getResidents(),
            mockApi.getLocks(),
            mockApi.getAssignments()
          ]);
          return {
            residents: residentsData,
            locks: locksData,
            assignments: assignmentsData
          };
        }
      );

      setResidents(data.residents);
      setLocks(data.locks);
      setAssignments(data.assignments);
    } catch (loadError) {
      console.error('Error loading data:', loadError);
      if (handleDataverseLoadError(loadError, navigate)) {
        return;
      }
      setResidents([]);
      setLocks([]);
      setAssignments([]);
      setError(getDataverseErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let cancelled = false;

    const resolveResidentOrigin = async () => {
      setSelectedLocks([]);
      setLockSearchTerm('');
      setResidentOrigin(null);

      if (!selectedResident) {
        setGeocodeStatus('idle');
        return;
      }

      const resident = residents.find((r) => r.residentId === selectedResident);
      const address = String(resident?.room || '').trim();

      if (!address || PLACEHOLDER_ADDRESS.test(address)) {
        setGeocodeStatus('no-address');
        return;
      }

      setGeocodeStatus('loading');
      const geocoded = await geocodeAddress(address);
      if (cancelled) {
        return;
      }

      if (!geocoded) {
        setResidentOrigin(null);
        setGeocodeStatus('failed');
        return;
      }

      setResidentOrigin(geocoded);
      setGeocodeStatus('ok');
    };

    resolveResidentOrigin();

    return () => {
      cancelled = true;
    };
  }, [selectedResident, residents]);

  const handleCreateAssignment = () => {
    if (!selectedResident) {
      alert('Vennligst velg en innbygger');
      return;
    }
    if (selectedLocks.length === 0) {
      alert('Vennligst velg minst én lås');
      return;
    }

    setOpenDialog(true);
  };

  const applyAssignmentsLocally = (residentId, lockIds, { remove = false } = {}) => {
    const lockIdSet = new Set(lockIds.map(String));

    setAssignments((prev) => {
      if (remove) {
        return prev.filter(
          (a) =>
            !(
              String(a.residentId) === String(residentId) &&
              lockIdSet.has(String(a.lockId))
            )
        );
      }
      const existing = new Set(prev.map((a) => `${a.residentId}_${a.lockId}`));
      const next = [...prev];
      for (const lockId of lockIds) {
        const id = `${residentId}_${lockId}`;
        if (existing.has(id) || existing.has(`${lockId}_${residentId}`)) {
          continue;
        }
        next.push({
          id: `${lockId}_${residentId}`,
          residentId,
          lockId,
          assignedDate: new Date().toISOString().split('T')[0],
          assignedBy: 'System',
          status: 'Aktiv'
        });
      }
      return next;
    });

    setResidents((prev) =>
      prev.map((resident) => {
        if (String(resident.residentId) !== String(residentId)) {
          return resident;
        }
        const current = resident.assignedLocks || [];
        const nextLocks = remove
          ? current.filter((id) => !lockIdSet.has(String(id)))
          : [...new Set([...current, ...lockIds])];
        return { ...resident, assignedLocks: nextLocks };
      })
    );

    setLocks((prev) =>
      prev.map((lock) => {
        if (!lockIdSet.has(String(lock.lockId))) {
          return lock;
        }
        const current = lock.assignedResidents || [];
        const nextResidents = remove
          ? current.filter((id) => String(id) !== String(residentId))
          : [...new Set([...current, residentId])];
        return { ...lock, assignedResidents: nextResidents };
      })
    );
  };

  const handleConfirmAssignment = async () => {
    try {
      setAdding(true);
      setError(null);

      const lockIds = [...selectedLocks];
      await Promise.all(
        lockIds.map((lockId) =>
          dataverseService.assignmentService.create({
            residentId: selectedResident,
            lockId
          })
        )
      );

      // Update UI immediately — avoid full residents/locks reload after each save
      applyAssignmentsLocally(selectedResident, lockIds);

      setOpenDialog(false);
      setSelectedResident('');
      setSelectedLocks([]);
      setResidentOrigin(null);
      setGeocodeStatus('idle');
      setLockSearchTerm('');
      setSuccessMessage('Tilordning(er) opprettet vellykket!');

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error creating assignment:', error);
      setError('Feil ved oppretting av tilordning. Prøv igjen.');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveClick = (assignmentId) => {
    setAssignmentToRemove(assignmentId);
    setRemoveConfirmOpen(true);
  };

  const handleRemoveConfirm = async () => {
    if (!assignmentToRemove) return;

    try {
      setRemoving(true);
      setError(null);
      await dataverseService.assignmentService.remove(assignmentToRemove);

      const [lockId, residentId] = String(assignmentToRemove).split('_');
      if (residentId && lockId) {
        applyAssignmentsLocally(residentId, [lockId], { remove: true });
      }

      setSuccessMessage('Tilordning fjernet vellykket!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setRemoveConfirmOpen(false);
      setAssignmentToRemove(null);
    } catch (error) {
      console.error('Error removing assignment:', error);
      setError('Feil ved fjerning av tilordning. Prøv igjen.');
    } finally {
      setRemoving(false);
    }
  };

  const handleRemoveCancel = () => {
    setRemoveConfirmOpen(false);
    setAssignmentToRemove(null);
  };

  const handleManageKeys = (resident) => {
    setKeyDialogResident(resident);
    setKeyDialogOpen(true);
  };

  const handleCloseKeyDialog = () => {
    setKeyDialogOpen(false);
    setKeyDialogResident(null);
  };

  const handleLockSelection = (lockId) => {
    setSelectedLocks(prev => 
      prev.includes(lockId) 
        ? prev.filter(id => id !== lockId)
        : [...prev, lockId]
    );
  };

  const getAvailableLocks = useCallback(() => {
    if (!selectedResident) return locks;
    
    const resident = residents.find(r => r.residentId === selectedResident);
    if (!resident) return locks;
    
    return locks.filter(lock => !resident.assignedLocks.includes(lock.lockId));
  }, [selectedResident, residents, locks]);

  const matchesLockSearch = useCallback((lock) => {
    const term = lockSearchTerm.trim().toLowerCase();
    if (!term) {
      return true;
    }

    return (
      lock.name?.toLowerCase().includes(term) ||
      lock.location?.toLowerCase().includes(term) ||
      lock.type?.toLowerCase().includes(term) ||
      lock.lockId?.toLowerCase().includes(term)
    );
  }, [lockSearchTerm]);

  const availableLocks = useMemo(() => getAvailableLocks(), [getAvailableLocks]);

  const partitionedLocks = useMemo(() => {
    if (geocodeStatus !== 'ok' || !residentOrigin) {
      return null;
    }

    return partitionNearbyLocks(availableLocks, residentOrigin, nearbyRadiusM);
  }, [availableLocks, geocodeStatus, residentOrigin, nearbyRadiusM]);

  const nearbyLocks = useMemo(
    () => (partitionedLocks?.nearby || []).filter(matchesLockSearch),
    [partitionedLocks, matchesLockSearch]
  );

  const otherLocks = useMemo(
    () => (partitionedLocks?.other || []).filter(matchesLockSearch),
    [partitionedLocks, matchesLockSearch]
  );

  const flatLocks = useMemo(
    () => availableLocks.filter(matchesLockSearch),
    [availableLocks, matchesLockSearch]
  );

  const showNearbySections = geocodeStatus === 'ok' && Boolean(residentOrigin);

  const handleSelectAllNearby = () => {
    const nearbyIds = nearbyLocks.map((lock) => lock.lockId);
    setSelectedLocks((prev) => [...new Set([...prev, ...nearbyIds])]);
  };

  const renderLockCheckbox = (lock) => (
    <ListItem key={lock.id || lock.lockId} sx={{ py: 0 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={selectedLocks.includes(lock.lockId)}
            onChange={() => handleLockSelection(lock.lockId)}
            size="small"
          />
        }
        label={
          <Box>
            <Typography variant="body2">
              {lock.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {lock.location} - {lock.type}
              {typeof lock.distanceMeters === 'number'
                ? ` • ${formatDistance(lock.distanceMeters)}`
                : ''}
            </Typography>
          </Box>
        }
      />
    </ListItem>
  );

  const getSelectedResidentData = () => {
    return residents.find(r => r.residentId === selectedResident);
  };

  // Get residents who have assignments
  const getResidentsWithAssignments = () => {
    const residentIds = [...new Set(assignments.map(a => a.residentId))];
    return residents.filter(r => residentIds.includes(r.residentId));
  };

  // Filter residents based on search term
  const getFilteredResidentsWithAssignments = () => {
    const residentsWithAssignments = getResidentsWithAssignments();
    if (!searchTerm) return residentsWithAssignments;
    
    return residentsWithAssignments.filter(resident =>
      resident.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resident.residentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resident.room.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const stats = {
    totalAssignments: assignments.length,
    residentsWithLocks: residents.filter(r => r.assignedLocks.length > 0).length,
    locksInUse: locks.filter(l => l.assignedResidents.length > 0).length,
    availableLocks: locks.filter(l => l.assignedResidents.length === 0).length
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" component="h1" fontWeight="600">
          Tilordninger
        </Typography>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
          onClick={loadData}
          disabled={loading}
          sx={{ borderRadius: 2 }}
        >
          {loading ? 'Oppdaterer…' : 'Oppdater'}
        </Button>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Administrer koblinger mellom innbyggere og låser
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" color="primary">
                {stats.totalAssignments}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Totale tilordninger
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" color="success.main">
                {stats.residentsWithLocks}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Innbyggere med låser
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" color="warning.main">
                {stats.locksInUse}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Låser i bruk
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" color="info.main">
                {stats.availableLocks}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tilgjengelige låser
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Create Assignment */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Opprett Tilordning
              </Typography>
              
              <Autocomplete
                fullWidth
                options={residents}
                value={residents.find((r) => r.residentId === selectedResident) || null}
                onChange={(_, next) => {
                  setSelectedResident(next?.residentId || '');
                  setSelectedLocks([]);
                  setLockSearchTerm('');
                }}
                getOptionLabel={(resident) =>
                  resident
                    ? `${resident.name}${resident.room ? ` (${resident.room})` : ''}`
                    : ''
                }
                isOptionEqualToValue={(a, b) => a?.residentId === b?.residentId}
                filterOptions={(options, { inputValue }) => {
                  const q = inputValue.trim().toLowerCase();
                  if (!q) {
                    return options;
                  }
                  return options.filter((resident) => {
                    const haystack = [
                      resident.name,
                      resident.room,
                      resident.residentId,
                      resident.nin,
                      resident.epjId
                    ]
                      .filter(Boolean)
                      .join(' ')
                      .toLowerCase();
                    return haystack.includes(q);
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Velg innbygger"
                    placeholder="Søk navn, adresse, ID…"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Search sx={{ ml: 1, mr: 0.5, color: 'action.active' }} fontSize="small" />
                          {params.InputProps.startAdornment}
                        </>
                      )
                    }}
                  />
                )}
                sx={{ mb: 2 }}
              />

              {selectedResident && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2">
                      Tilgjengelige låser:
                    </Typography>
                    {showNearbySections && (
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={nearbyRadiusM}
                        onChange={(_, value) => {
                          if (value != null) {
                            setNearbyRadiusM(value);
                          }
                        }}
                        aria-label="Nærhetsradius"
                      >
                        {NEARBY_RADIUS_OPTIONS.map((radius) => (
                          <ToggleButton key={radius} value={radius}>
                            {radius >= 1000 ? `${radius / 1000} km` : `${radius} m`}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    )}
                  </Box>

                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Søk låser..."
                    value={lockSearchTerm}
                    onChange={(e) => setLockSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} fontSize="small" />
                    }}
                    sx={{ mb: 1 }}
                  />

                  {geocodeStatus === 'loading' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="text.secondary">
                        Finner posisjon for adressen...
                      </Typography>
                    </Box>
                  )}

                  {(geocodeStatus === 'failed' || geocodeStatus === 'no-address') && (
                    <Alert severity="info" sx={{ mb: 1 }}>
                      {geocodeStatus === 'no-address'
                        ? 'Ingen adresse registrert for innbyggeren. Viser alle tilgjengelige låser.'
                        : 'Kunne ikke finne posisjon for adressen. Viser alle tilgjengelige låser.'}
                    </Alert>
                  )}

                  {showNearbySections && residentOrigin?.formattedAddress && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Posisjon: {residentOrigin.formattedAddress}
                    </Typography>
                  )}

                  {showNearbySections ? (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>
                          Foreslått i nærheten ({nearbyLocks.length})
                        </Typography>
                        <Button
                          size="small"
                          onClick={handleSelectAllNearby}
                          disabled={nearbyLocks.length === 0}
                        >
                          Velg alle foreslåtte
                        </Button>
                      </Box>
                      <Paper variant="outlined" sx={{ maxHeight: 180, overflow: 'auto', mb: 1.5 }}>
                        <List dense>
                          {nearbyLocks.length === 0 ? (
                            <ListItem>
                              <ListItemText
                                primary="Ingen låser innen valgt radius"
                                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                              />
                            </ListItem>
                          ) : (
                            nearbyLocks.map(renderLockCheckbox)
                          )}
                        </List>
                      </Paper>

                      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                        Øvrige låser ({otherLocks.length})
                      </Typography>
                      <Paper variant="outlined" sx={{ maxHeight: 180, overflow: 'auto' }}>
                        <List dense>
                          {otherLocks.length === 0 ? (
                            <ListItem>
                              <ListItemText
                                primary="Ingen øvrige låser"
                                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                              />
                            </ListItem>
                          ) : (
                            otherLocks.map(renderLockCheckbox)
                          )}
                        </List>
                      </Paper>
                    </>
                  ) : (
                    <Paper variant="outlined" sx={{ maxHeight: 220, overflow: 'auto' }}>
                      <List dense>
                        {flatLocks.length === 0 ? (
                          <ListItem>
                            <ListItemText
                              primary="Ingen tilgjengelige låser"
                              primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                            />
                          </ListItem>
                        ) : (
                          flatLocks.map(renderLockCheckbox)
                        )}
                      </List>
                    </Paper>
                  )}
                </Box>
              )}

              <Button
                fullWidth
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateAssignment}
                disabled={!selectedResident || selectedLocks.length === 0}
                sx={{ mt: 2 }}
              >
                Opprett Tilordning
              </Button>

              {getSelectedResidentData() && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Valgt innbygger:
                  </Typography>
                  <Typography variant="body2">
                    <strong>{getSelectedResidentData().name}</strong><br />
                    Adresse: {getSelectedResidentData().room}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Current Assignments */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="600">
                  Aktive Tilordninger
                </Typography>
                <TextField
                  size="small"
                  placeholder="Søk innbyggere..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
                  }}
                  sx={{ width: 250 }}
                />
              </Box>
              
              {assignments.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    Ingen aktive tilordninger
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {getFilteredResidentsWithAssignments().map((resident) => (
                    <Grid item xs={12} key={resident.id}>
                      <ResidentAssignmentCard
                        resident={resident}
                        assignments={assignments}
                        locks={locks}
                        onRemoveAssignment={handleRemoveClick}
                        onManageKeys={handleManageKeys}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
              
              {searchTerm && getFilteredResidentsWithAssignments().length === 0 && assignments.length > 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    Ingen innbyggere funnet som matcher søket
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bekreft Tilordning</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Du er i ferd med å tilordne følgende låser til <strong>{getSelectedResidentData()?.name}</strong>:
          </Typography>
          
          <List>
            {selectedLocks.map(lockId => {
              const lock = locks.find(l => l.lockId === lockId);
              return (
                <ListItem key={lockId}>
                  <ListItemText
                    primary={lock?.name}
                    secondary={`${lock?.location} - ${lock?.type}`}
                  />
                </ListItem>
              );
            })}
          </List>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            Dette vil gi innbyggeren tilgang til de valgte låsene.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={adding}>
            Avbryt
          </Button>
          <Button 
            onClick={handleConfirmAssignment} 
            variant="contained"
            disabled={adding}
            startIcon={adding ? <CircularProgress size={20} /> : null}
          >
            {adding ? 'Oppretter...' : 'Bekreft Tilordning'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* Remove Confirmation Dialog */}
      <Dialog
        open={removeConfirmOpen}
        onClose={handleRemoveCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Bekreft fjerning av tilordning
        </DialogTitle>
        <DialogContent>
          <Typography>
            Er du sikker på at du vil fjerne denne tilordningen?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Innbyggeren vil miste tilgangen til låsen.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRemoveCancel} disabled={removing}>
            Avbryt
          </Button>
          <Button 
            onClick={handleRemoveConfirm} 
            variant="contained"
            color="error"
            disabled={removing}
            startIcon={removing ? <CircularProgress size={20} /> : null}
          >
            {removing ? 'Fjerner...' : 'Fjern'}
          </Button>
        </DialogActions>
      </Dialog>

      <KeyRequestDialog
        open={keyDialogOpen}
        onClose={handleCloseKeyDialog}
        scope={KEY_REQUEST_SCOPE.ALL_LOCKS_FOR_RESIDENT}
        resident={keyDialogResident}
        onSuccess={handleCloseKeyDialog}
      />
    </Box>
  );
};

export default AssignmentsPage;
